// setup-ai-orchestrator-model-tier-picker.ts
//
// Setup-picker cho tool `ai-orchestrator` (team-ai-pack). Chạy lúc
// `avatar tools enable ai-orchestrator` (wired trong enableToolByName).
//
// Flow (xem team-ai-pack/tools-design/avatar-ai-orchestrator-explained.html §6):
//   1) Đọc .claude/settings.json → detect provider + baseUrl + token.
//   2) Lấy danh sách model:
//        - llmlite/anthropic (có baseUrl + token): GET /v1/models (tên đầy đủ).
//        - subscription (không token/baseUrl): alias cứng ["opus","sonnet","haiku"].
//   3) count == 1 → route VÔ NGHĨA → log + return false (caller skip enable).
//   4) count >= 2 → hỏi 3 câu picker: tier Khó / Vừa / Dễ → model nào.
//   5) Ghi state map vào .claude/state/ai-orchestrator-model-map.json.
//   6) return true → caller fallthrough merge manifest.
//
// Idempotent cho `avatar sync`: nếu state map ĐÃ tồn tại + valid (đủ 3 tier) →
// skip hỏi, return true (giữ map cũ). Tránh sync hỏi lại 3 câu interactive.
//
// Non-TTY (CI / --auto-yes): không thể prompt → log + return false (skip).
//
// KHÔNG hardcode key: token đọc từ settings.json.env / process.env (như các
// module provider khác). Fetch fail (VPN tắt...) → log + return false (không vỡ).

import { join } from "node:path";
import { select } from "@inquirer/prompts";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";
import { resolveTierSlotModelEnv } from "./resolve-claude-code-alias-to-bare-model-env.js";
import { fetchAvailableModels } from "./setup-llmlite-api-key-and-model.js";
import { log } from "./terminal-logger.js";
import { withSettingsFileLock } from "./with-settings-file-lock.js";
import { SECRET_FILE_MODE } from "./write-claude-settings-json-per-project.js";

const STATE_RELATIVE_PATH = ".claude/state/ai-orchestrator-model-map.json";

// Subscription không query được /v1/models → alias chuẩn cho user map tier.
const SUBSCRIPTION_ALIASES = ["opus", "sonnet", "haiku"];

interface ModelTierMap {
  hard: string;
  standard: string;
  fast: string;
}

interface OrchestratorState {
  map: ModelTierMap;
  provider: string;
  source: string;
  updatedAt: number;
}

interface DetectedProvider {
  provider: "subscription" | "llmlite" | "anthropic";
  baseUrl: string | null;
  token: string | null;
}

function statePath(workspacePath: string): string {
  return join(workspacePath, STATE_RELATIVE_PATH);
}

// Đọc state map hiện có. Thiếu / hỏng / thiếu tier → null.
async function readExistingStateMap(workspacePath: string): Promise<ModelTierMap | null> {
  const path = statePath(workspacePath);
  if (!(await pathExists(path))) return null;
  try {
    const data = await readJson<Partial<OrchestratorState>>(path);
    const m = data.map;
    if (
      m &&
      typeof m.hard === "string" &&
      typeof m.standard === "string" &&
      typeof m.fast === "string"
    ) {
      return { hard: m.hard, standard: m.standard, fast: m.fast };
    }
    return null;
  } catch {
    return null;
  }
}

// Detect provider + baseUrl + token từ settings.json (pattern theo
// run-gitnexus-wiki-conditional.ts). Thiếu settings → coi như subscription.
async function detectProvider(workspacePath: string): Promise<DetectedProvider> {
  const settingsPath = join(workspacePath, ".claude", "settings.json");
  if (!(await pathExists(settingsPath))) {
    return { provider: "subscription", baseUrl: null, token: null };
  }
  try {
    const settings = await readJson<{
      env?: Record<string, unknown>;
      avatarProvider?: unknown;
    }>(settingsPath);
    const env = settings.env || {};
    const baseUrl = typeof env.ANTHROPIC_BASE_URL === "string" ? env.ANTHROPIC_BASE_URL : null;
    const providerHint =
      typeof settings.avatarProvider === "string" ? settings.avatarProvider : null;

    // Không có baseUrl → subscription (OAuth, không query model list được).
    if (!baseUrl) {
      return { provider: "subscription", baseUrl: null, token: null };
    }

    const resolveKey = (name: string): string | null => {
      const fromProcess = process.env[name];
      if (typeof fromProcess === "string" && fromProcess.length > 0) return fromProcess;
      const fromSettings = env[name];
      if (typeof fromSettings === "string" && fromSettings.length > 0) return fromSettings;
      return null;
    };

    let baseUrlSuggestsAnthropic = false;
    try {
      baseUrlSuggestsAnthropic = new URL(baseUrl).hostname === "api.anthropic.com";
    } catch {
      baseUrlSuggestsAnthropic = false;
    }
    const provider: "anthropic" | "llmlite" =
      providerHint === "anthropic"
        ? "anthropic"
        : providerHint === "llmlite"
          ? "llmlite"
          : baseUrlSuggestsAnthropic
            ? "anthropic"
            : "llmlite";

    const token =
      provider === "anthropic"
        ? resolveKey("ANTHROPIC_API_KEY")
        : resolveKey("ANTHROPIC_AUTH_TOKEN");

    return { provider, baseUrl, token };
  } catch {
    // Settings hỏng → fallback subscription (an toàn, dùng alias).
    return { provider: "subscription", baseUrl: null, token: null };
  }
}

// Lọc model dùng được để phân loại việc: ưu tiên claude-* (tránh map vào model
// non-Claude vô tình có trên key); nếu không có claude nào → giữ nguyên list.
// Export để init dùng CÙNG logic khi đếm "đủ model phân loại việc không?" (DRY).
export function selectTierableModels(rawModels: string[]): string[] {
  const claude = rawModels.filter((m) => m.toLowerCase().includes("claude"));
  return claude.length > 0 ? claude : rawModels;
}

// Lấy danh sách model cho picker theo provider.
//   subscription → alias cứng.
//   llmlite/anthropic có token+baseUrl → fetch /v1/models.
//   thiếu token → fallback alias (không fail enable).
async function resolveModelList(
  detected: DetectedProvider,
): Promise<{ models: string[]; source: string }> {
  if (detected.provider === "subscription" || !detected.baseUrl || !detected.token) {
    return { models: [...SUBSCRIPTION_ALIASES], source: "alias" };
  }
  const all = await fetchAvailableModels(detected.baseUrl, detected.token);
  return { models: selectTierableModels(all), source: "/v1/models" };
}

// Hỏi 1 tier → model. Cho phép chọn TRÙNG (key 2 model vẫn map đủ 3 tier).
async function promptTierModel(tierLabel: string, models: string[]): Promise<string> {
  return await select({
    message: `Tier ${tierLabel} → chọn model:`,
    choices: models.map((m) => ({ name: m, value: m })),
  });
}

async function writeStateMap(
  workspacePath: string,
  map: ModelTierMap,
  provider: string,
  source: string,
): Promise<void> {
  const state: OrchestratorState = {
    map,
    provider,
    source,
    updatedAt: Math.floor(Date.now() / 1000),
  };
  await writeJsonAtomic(statePath(workspacePath), state);
}

// Cơ chế ALIAS = SLOT TIER (xem resolve-claude-code-alias-to-bare-model-env.ts):
// merge env ANTHROPIC_DEFAULT_{OPUS,SONNET,HAIKU}_MODEL = map.{hard,standard,fast}
// vào settings.json → lệnh savecost spawn alias (opus/sonnet/haiku) expand thành
// đúng model user map, bất kể provider (claude-*, deepseek-*, ...). Đồng thời
// fix 403 dated-ID (env ghi đè dated default của binary).
//
// Chỉ áp cho llmlite/anthropic (gateway dùng tên model đầy đủ). Subscription dùng
// alias OAuth — KHÔNG cần (map là alias sẵn, env sẽ thành vòng lặp alias→alias).
// Fail-mềm: lỗi đọc/ghi settings → log warn, KHÔNG chặn enable.
async function mergeTierSlotModelEnvIntoSettings(
  workspacePath: string,
  map: ModelTierMap,
  provider: string,
): Promise<void> {
  if (provider === "subscription") return;
  const slotEnv = resolveTierSlotModelEnv(map);

  const settingsPath = join(workspacePath, ".claude", "settings.json");
  // #164 writer #6 — BRACKET NẰM NGOÀI `try` (B34), KHÔNG được đảo lại:
  // cặp read/write dưới đây nằm trọn trong `try{...}catch{log.warn}` NUỐT MỌI LỖI.
  // Đặt bracket TRONG try ⇒ SettingsLockTimeoutError cũng bị nuốt ⇒ writer này
  // thành fail-OPEN im lặng, trái chốt (e). Để bracket ngoài + try trong mutator:
  // lỗi đọc/ghi vẫn fail-mềm y như cũ (giữ R5), CHỈ lock-timeout mới thoát ra.
  //
  // ⚠️ ĐÂY LÀ THAY ĐỔI HÀNH VI CÓ CHỦ Ý, không phải sơ suất: trước đây hàm này
  // KHÔNG BAO GIỜ throw; sau vá, SettingsLockTimeoutError sẽ nổi lên
  // runAiOrchestratorSetupPicker → enableToolByName → caller. Đánh đổi theo chốt (e)
  // fail-closed: thà `avatar tools enable ai-orchestrator` báo lỗi rõ còn hơn ghi đè
  // mất env của tiến trình khác. Đường `avatar sync` đã được R8 đỡ (catch
  // SettingsLockTimeoutError per-tool ⇒ chỉ bỏ qua đúng tool đó).
  await withSettingsFileLock(workspacePath, async () => {
    try {
      if (!(await pathExists(settingsPath))) return; // không tự tạo settings
      const settings = await readJson<{ env?: Record<string, string> }>(settingsPath);
      settings.env = { ...(settings.env || {}), ...slotEnv };
      await writeJsonAtomic(settingsPath, settings, SECRET_FILE_MODE);
      log.dim(
        "ai-orchestrator: set env slot tier (OPUS=hard, SONNET=standard, HAIKU=fast) — alias spawn expand đúng model đã map.",
      );
    } catch (err) {
      log.warn(
        `ai-orchestrator: không ghi được tier-slot env (${(err as Error).message}) — route có thể chạy sai model. Bỏ qua, tool vẫn bật.`,
      );
    }
  });
}

// Main entry — gọi từ enableToolByName special-branch.
// Trả true → caller fallthrough merge manifest. false → caller skip enable.
//
// opts.forceRefresh: bỏ qua skip-if-exists → hỏi lại 3 loại việc + ghi đè map.
// Dùng khi `avatar ai setup` đổi key (model cũ có thể không dùng được nữa).
// Mặc định false → sync/re-apply giữ map cũ (không hỏi lại interactive).
export async function runAiOrchestratorSetupPicker(
  workspacePath: string,
  opts: { forceRefresh?: boolean } = {},
): Promise<boolean> {
  // (0) Sync re-apply / re-enable: map đã có + valid → giữ nguyên, không hỏi lại.
  //     forceRefresh=true (ai setup đổi key) → bỏ qua, hỏi lại để refresh map.
  if (!opts.forceRefresh) {
    const existing = await readExistingStateMap(workspacePath);
    if (existing) {
      log.dim("ai-orchestrator: state map đã tồn tại — giữ map cũ (không hỏi lại).");
      // Re-apply env slot tier từ map cũ (idempotent, không prompt) — heal case
      // env bị thiếu/strip (sync, sửa settings tay, bản CLI cũ chưa ghi đủ).
      const detected = await detectProvider(workspacePath);
      await mergeTierSlotModelEnvIntoSettings(workspacePath, existing, detected.provider);
      return true;
    }
  }

  // (1) Non-TTY → không prompt được 3 tier interactive.
  if (!process.stdout.isTTY) {
    log.warn(
      "ai-orchestrator cần interactive setup (chọn model cho 3 tier) — bỏ qua trong môi trường non-TTY. " +
        "Chạy 'avatar tools enable ai-orchestrator' trong terminal thật để cài.",
    );
    return false;
  }

  // (2) Detect provider + (3) lấy model list.
  const detected = await detectProvider(workspacePath);
  let models: string[];
  let source: string;
  try {
    const resolved = await resolveModelList(detected);
    models = resolved.models;
    source = resolved.source;
  } catch (err) {
    log.error(
      `ai-orchestrator: không lấy được danh sách model (${(err as Error).message}). Không bật tool. Kiểm tra VPN/gateway rồi thử lại.`,
    );
    return false;
  }

  // (4) count == 1 → route vô nghĩa → skip enable.
  if (models.length === 1) {
    log.warn(
      `ai-orchestrator: key chỉ có 1 model (${models[0]}) — route vô nghĩa khi chỉ 1 model. KHÔNG bật tool.`,
    );
    return false;
  }

  log.info(
    `ai-orchestrator: ${models.length} model khả dụng (${detected.provider}). Chọn model cho mỗi loại việc (Khó/Vừa/Dễ). Có thể chọn trùng.`,
  );

  // (5) Hỏi 3 loại việc. Thứ tự việc Khó → Vừa → Dễ (HTML §6).
  let map: ModelTierMap;
  try {
    const hard = await promptTierModel("Việc khó (model mạnh)", models);
    const standard = await promptTierModel("Việc vừa (model cân bằng)", models);
    const fast = await promptTierModel("Việc dễ (model rẻ)", models);
    map = { hard, standard, fast };
  } catch {
    // User Ctrl-C giữa chừng → hủy, không bật.
    log.dim("ai-orchestrator: hủy chọn model — không bật tool.");
    return false;
  }

  // (6) Ghi state map.
  try {
    await writeStateMap(workspacePath, map, detected.provider, source);
  } catch (err) {
    log.error(
      `ai-orchestrator: ghi state map thất bại (${(err as Error).message}). Không bật tool.`,
    );
    return false;
  }

  // (6.5) ALIAS = SLOT TIER: merge env OPUS/SONNET/HAIKU = map hard/standard/fast.
  await mergeTierSlotModelEnvIntoSettings(workspacePath, map, detected.provider);

  log.success(
    `ai-orchestrator: đã map tier → model. Khó=${map.hard} · Vừa=${map.standard} · Dễ=${map.fast}`,
  );
  return true;
}
